// Voicing spreads: the different ways to open a 4-note chord across registers.
// In Tymoczko's OPTIC language a "voicing type" is an octave-displacement class;
// Ted Greene's V-System catalogued the guitar-practical families (close = V-1,
// drop-2 = V-2 — the family this whole app is built on). Here we enumerate every
// distinct spread within two octaves, name the famous drops, and check which
// ones the guitar can actually finger.

import { frettedMidi } from './tuning';

export interface Spread {
  /** Semitone offsets from the bass voice, ascending, starting at 0. */
  pitches: number[];
  /** Adjacent-voice gaps in semitones. */
  gaps: number[];
  /** Famous name, when it is one of the classic drops of the close stack. */
  name?: string;
  span: number;
}

const sortAsc = (xs: number[]) => [...xs].sort((a, b) => a - b);
const rebase = (xs: number[]) => {
  const s = sortAsc(xs);
  return s.map((v) => v - s[0]);
};
const gapsOf = (p: number[]) => p.slice(1).map((v, i) => v - p[i]);

/** The close root-position stack: the chord's pcs packed ascending from the root. */
export function closeStack(pcs: number[]): number[] {
  const root = pcs[0];
  return sortAsc(pcs.map((pc) => (((pc - root) % 12) + 12) % 12));
}

/** Lower the given voices (counted 1..4 from the TOP of the close stack) an octave. */
function drop(close: number[], fromTop: number[]): number[] {
  const dropped = close.map((v, i) => {
    const posFromTop = close.length - i; // top voice = 1
    return fromTop.includes(posFromTop) ? v - 12 : v;
  });
  return rebase(dropped);
}

/** The named drop family, derived from the definition (not hand-listed). */
export function namedSpreads(pcs: number[]): { name: string; pitches: number[] }[] {
  const close = closeStack(pcs);
  return [
    { name: 'Close', pitches: rebase(close) },
    { name: 'Drop 2', pitches: drop(close, [2]) },
    { name: 'Drop 3', pitches: drop(close, [3]) },
    { name: 'Drop 2 & 4', pitches: drop(close, [2, 4]) },
    { name: 'Drop 2 & 3', pitches: drop(close, [2, 3]) },
  ];
}

/**
 * Every distinct spread of the chord within a two-octave span: each voice may
 * sit in any octave, voicings are identified by their gap pattern. The classic
 * drops get their names; the rest are the wide-spread country Greene's higher
 * V-groups chart.
 */
export function enumerateSpreads(pcs: number[]): Spread[] {
  const close = closeStack(pcs);
  const named = namedSpreads(pcs);
  const out = new Map<string, Spread>();
  const lifts = [0, 12, 24];
  for (const a of lifts) {
    for (const b of lifts) {
      for (const c of lifts) {
        for (const d of lifts) {
          const pitches = rebase([close[0] + a, close[1] + b, close[2] + c, close[3] + d]);
          const span = pitches[pitches.length - 1];
          if (span > 24) continue;
          const gaps = gapsOf(pitches);
          if (gaps.some((g) => g === 0)) continue; // two voices collapsed
          const key = gaps.join(',');
          if (!out.has(key)) {
            const name = named.find((n) => n.pitches.join(',') === pitches.join(','))?.name;
            out.set(key, { pitches, gaps, name, span });
          }
        }
      }
    }
  }
  return [...out.values()].sort((x, y) => (x.name ? -1 : 0) - (y.name ? -1 : 0) || x.span - y.span);
}

const MAX_FRET = 17;
const HAND_SPAN = 5;

/**
 * Can the guitar finger this spread? Search every ascending 4-string subset for
 * exact-midi placements within a five-fret hand span. Returns the frets (per
 * string, null = muted) of the lowest playable position, or null.
 */
export function findGuitarFingering(
  bassMidi: number,
  pitches: number[],
): (number | null)[] | null {
  const midis = pitches.map((p) => bassMidi + p);
  let best: { frets: (number | null)[]; low: number } | null = null;
  // choose 4 of 6 strings, ascending
  for (let s0 = 0; s0 <= 2; s0++)
    for (let s1 = s0 + 1; s1 <= 3; s1++)
      for (let s2 = s1 + 1; s2 <= 4; s2++)
        for (let s3 = s2 + 1; s3 <= 5; s3++) {
          const subset = [s0, s1, s2, s3];
          const frets = subset.map((s, i) => midis[i] - frettedMidi(s, 0));
          if (frets.some((f) => f < 0 || f > MAX_FRET)) continue;
          const fretted = frets.filter((f) => f > 0);
          const lo = fretted.length ? Math.min(...fretted) : 0;
          const hi = fretted.length ? Math.max(...fretted) : 0;
          if (hi - lo >= HAND_SPAN) continue;
          const full: (number | null)[] = [null, null, null, null, null, null];
          subset.forEach((s, i) => {
            full[s] = frets[i];
          });
          if (!best || lo < best.low) best = { frets: full, low: lo };
        }
  return best?.frets ?? null;
}
